import { useState } from 'react';
import { Icon, type IconName } from '../primitives/Icon';
import { Linking, Pressable, type GestureResponderEvent } from 'react-native';
import { Text, XStack, YStack } from 'tamagui';
import { useThemeColors } from '../hooks/useThemeColors';
import { placeMapUrl, type PlaceMapInput } from '../utils/placeMapUrl';

export type PlaceAddressVariant = 'compact' | 'form';

/** Contact channels shown under the collapsed Contact disclosure.
 *  All three are optional — rows render only when set. Strings are
 *  display-formatted (e.g. "+81 3 5555 1234"); link handlers strip
 *  whitespace from phone for the `tel:` URI. */
export interface PlaceContactInfo {
  phone?: string | undefined;
  website?: string | undefined;
  email?: string | undefined;
}

export interface PlaceAddressProps {
  /** Place-shaped object. Both `Place` and `PlaceRef` satisfy the
   *  duck-typed shape; PlaceRef callers map `ref.id → catalogId`. */
  place: PlaceMapInput;
  /** Optional contact channels. When any field is set a "Contact"
   *  disclosure toggle renders below the address (collapsed by
   *  default); expanded rows are tappable into the platform handler
   *  (`tel:`, `https://`, `mailto:`). */
  contact?: PlaceContactInfo | undefined;
  /** `compact` — card style: location-on icon + text + open-in-new icon.
   *  `form` — bigger text, open-in-new only (FormRow supplies the
   *  location-on icon). Default `compact`. */
  variant?: PlaceAddressVariant | undefined;
  /** Vertical spacing to the previous block in the parent. */
  marginTop?: number | undefined;
  /** Number of address text lines before truncation. Defaults to 2. */
  numberOfLines?: number | undefined;
  /** Plain-text shown when `place.address` is missing. When omitted,
   *  the component renders nothing for missing-address inputs. */
  fallback?: string | undefined;
}

function hasAnyContact(c: PlaceContactInfo | undefined): c is PlaceContactInfo {
  if (!c) return false;
  return Boolean(c.phone?.trim() || c.website?.trim() || c.email?.trim());
}

/**
 * Shared rendering for a place's address as a tappable link to Google
 * Maps. Replaces several inline copies of the same icon + text + maps
 * URL pattern. When `contact` is supplied with at least one channel,
 * a collapsed "Contact" disclosure appears below the address.
 */
export function PlaceAddress({
  place,
  contact,
  variant = 'compact',
  marginTop,
  numberOfLines = 2,
  fallback,
}: PlaceAddressProps) {
  const theme = useThemeColors();
  const [contactOpen, setContactOpen] = useState(false);
  const address = place.address;
  const showContact = hasAnyContact(contact);

  if (!address && !showContact) {
    if (!fallback) return null;
    return (
      <Text
        fontSize={variant === 'form' ? 15 : 12}
        color="$textSubdued"
        marginTop={marginTop}
        numberOfLines={numberOfLines}
      >
        {fallback}
      </Text>
    );
  }

  // stopPropagation here so the address (and contact rows) can be
  // tapped inside an outer Pressable (e.g. the day-list row that opens
  // place detail) without firing the outer onPress on web — react-native
  // bubbles click events on react-native-web.
  const handleAddressPress = (e?: GestureResponderEvent) => {
    e?.stopPropagation?.();
    void Linking.openURL(placeMapUrl(place));
  };

  const addressNode = !address ? null : variant === 'form' ? (
    <Pressable
      onPress={handleAddressPress}
      accessibilityRole="link"
      accessibilityLabel="Open address in Maps"
      style={({ pressed }) => [
        {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 6,
          marginTop: marginTop ?? 2,
        },
        pressed && { opacity: 0.7 },
      ]}
    >
      <Text
        flex={1}
        fontSize={15}
        color="$textOnSurface"
        fontWeight="600"
        numberOfLines={numberOfLines}
      >
        {address}
      </Text>
      <Icon name="open-in-new" size={16} color={theme.textSubdued} />
    </Pressable>
  ) : (
    <Pressable
      onPress={handleAddressPress}
      accessibilityRole="link"
      accessibilityLabel="Open address in Maps"
      {...({ dataSet: { printFlat: 'true' } } as Record<string, unknown>)}
      style={({ pressed }) => [
        {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 6,
          paddingVertical: 2,
          marginTop,
        },
        pressed && { opacity: 0.6 },
      ]}
    >
      <Icon name="location-on" size={13} color={theme.textSubdued} />
      <Text flex={1} fontSize={12} color="$textSubdued" numberOfLines={numberOfLines}>
        {address}
      </Text>
      <YStack dataSet={{ printHide: 'true' }}>
        <Icon name="open-in-new" size={12} color={theme.textSubdued} />
      </YStack>
    </Pressable>
  );

  const contactBlock = showContact ? (
    <YStack marginTop={address ? 4 : (marginTop ?? 0)} gap={4} dataSet={{ printFlat: 'true' }}>
      <Pressable
        onPress={(e) => {
          e?.stopPropagation?.();
          setContactOpen((v) => !v);
        }}
        accessibilityRole="button"
        accessibilityState={{ expanded: contactOpen }}
        accessibilityLabel={contactOpen ? 'Hide contact info' : 'Show contact info'}
        hitSlop={6}
        {...({ dataSet: { printHide: 'true' } } as Record<string, unknown>)}
        style={({ pressed }) => [
          {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 4,
            paddingVertical: 2,
            alignSelf: 'flex-start',
          },
          pressed && { opacity: 0.6 },
        ]}
      >
        <Icon
          name={contactOpen ? 'expand-less' : 'expand-more'}
          size={14}
          color={theme.textSubdued}
        />
        <Text fontSize={12} color="$textSubdued" fontWeight="600">
          Contact
        </Text>
      </Pressable>
      {/* Rows are always rendered so the print stylesheet can reveal
       *  them via [data-print-show-collapsed] (see usePrintFriendly).
       *  On screen, inline `display: none` keeps them hidden until the
       *  user expands; print CSS overrides with `!important`. */}
      <YStack
        gap={4}
        paddingLeft={2}
        dataSet={{ printShowCollapsed: 'true' }}
        style={{ display: contactOpen ? 'flex' : 'none' }}
      >
        {contact?.phone?.trim() ? (
          <ContactRow
            icon="call"
            label={contact.phone}
            onPress={(e) => {
              e?.stopPropagation?.();
              const dial = contact.phone!.replace(/\s+/g, '');
              void Linking.openURL(`tel:${dial}`);
            }}
            accessibilityLabel={`Call ${contact.phone}`}
          />
        ) : null}
        {contact?.website?.trim() ? (
          <ContactRow
            icon="language"
            label={prettifyUrl(contact.website)}
            onPress={(e) => {
              e?.stopPropagation?.();
              void Linking.openURL(ensureHttp(contact.website!));
            }}
            accessibilityLabel={`Open website ${contact.website}`}
          />
        ) : null}
        {contact?.email?.trim() ? (
          <ContactRow
            icon="mail-outline"
            label={contact.email}
            onPress={(e) => {
              e?.stopPropagation?.();
              void Linking.openURL(`mailto:${contact.email!.trim()}`);
            }}
            accessibilityLabel={`Email ${contact.email}`}
          />
        ) : null}
      </YStack>
    </YStack>
  ) : null;

  return (
    <YStack>
      {addressNode}
      {contactBlock}
    </YStack>
  );
}

function ContactRow({
  icon,
  label,
  onPress,
  accessibilityLabel,
}: {
  icon: IconName;
  label: string;
  onPress: (event?: GestureResponderEvent) => void;
  accessibilityLabel: string;
}) {
  const theme = useThemeColors();
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="link"
      accessibilityLabel={accessibilityLabel}
      style={({ pressed }) => [
        {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 6,
          paddingVertical: 2,
        },
        pressed && { opacity: 0.6 },
      ]}
    >
      <Icon name={icon} size={13} color={theme.textSubdued} />
      <Text flex={1} fontSize={12} color="$textOnSurface" numberOfLines={1}>
        {label}
      </Text>
      <XStack dataSet={{ printHide: 'true' }}>
        <Icon name="open-in-new" size={12} color={theme.textSubdued} />
      </XStack>
    </Pressable>
  );
}

/** Strip scheme + trailing slash for compact display. The link itself
 *  still opens the original URL (after `ensureHttp` for schemeless
 *  inputs). */
function prettifyUrl(url: string): string {
  return url
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/$/, '');
}

/** Normalize a website value into something Linking.openURL can act on.
 *  - Absolute http(s) URL → pass through.
 *  - Protocol-relative (`//host/...`) → prepend `https:`.
 *  - Already-prefixed with some other scheme (`mailto:`, `ftp:`, etc.)
 *    → pass through; the OS decides what to do.
 *  - Otherwise → assume a bare host/path and prepend `https://`.
 *  Guards against the previous `https:////example.com` bug for inputs
 *  that already started with `//` or another scheme. */
function ensureHttp(url: string): string {
  const t = url.trim();
  if (/^https?:\/\//i.test(t)) return t;
  if (t.startsWith('//')) return `https:${t}`;
  if (/^[a-z][a-z0-9+.-]*:/i.test(t)) return t;
  return `https://${t}`;
}
